import { produce } from 'immer';
import { ChartsTheme } from '@/components/model/theme';
import { GenericThresholdOptions, isSingleValueData, QueryDefinition, StepOptions, SupportedDataType } from '@/core';
import { QueryPluginContext, QueryPluginGetDataBasicResponse } from '@/plugin-system/model';

/**
 * Data types that support the per-query "render as threshold" feature. The query
 * editor shows the threshold toggle only for these types; `applyQueryThreshold`
 * is a no-op for other types, so UI and data handling stay aligned.
 *
 * - `TimeSeries` and `SingleValueTimeSeries` get Series behavior: returned series
 *   are tagged via `series[i].config.threshold` so panels can render them as
 *   dashed reference lines and keep them out of stack accumulation total.
 * - `SingleValue` gets Constant behavior: a `metadata.thresholds` entry is built
 *   from each pre-reduced scalar value, sorted/merged into the panel thresholds
 *   downstream by `mergeThresholds`.
 */
export const THRESHOLD_SUPPORTED_DATA_TYPES: ReadonlySet<SupportedDataType> = new Set([
  'TimeSeries',
  'SingleValueTimeSeries',
  'SingleValue',
]);

/**
 * Augments a basic data-query response based on the query's `threshold` flag.
 */
export function applyQueryThreshold<T extends QueryPluginGetDataBasicResponse<SupportedDataType>>(
  data: T,
  {
    definition,
    context,
    chartsTheme,
  }: {
    definition: QueryDefinition;
    context: QueryPluginContext;
    chartsTheme: ChartsTheme;
  }
): T {
  const threshold = definition.spec.threshold;

  if (threshold === undefined) return data;

  if (context.featureFlags?.enableThresholdQueryOption !== true) return data;

  // Compare mode is intentionally out of scope for threshold queries:
  // `useRunDataQueries` strips `compareDurationInMs` and forces `getData` for
  // these definitions so the tooltip shows a single dashed reference line
  // instead of a primary + offset pair. This guard remains as a defensive
  // safety net for any caller that bypasses that routing.
  if (context.compareDurationInMs !== undefined && context.compareDurationInMs !== null) return data;

  const dataType = data.type as SupportedDataType | undefined;
  if (!dataType || !THRESHOLD_SUPPORTED_DATA_TYPES.has(dataType)) return data;

  const color = threshold.color ?? chartsTheme.thresholds.defaultColor;
  // The threshold's display name is sourced from the query's alias.
  const label = definition.spec.alias;

  if (dataType === 'SingleValue') {
    return applyConstantBehavior(data, { color, label });
  }

  return applySeriesBehavior(data, { color, label });
}

/**
 * Build dynamic Constant threshold steps from a pre-reduced `SingleValue` payload.
 * Multiple series in one query produce one step per series so users can flag a
 * grouped query and get a step per dimension.
 */
function applyConstantBehavior<T extends QueryPluginGetDataBasicResponse<SupportedDataType>>(
  data: T,
  { color, label }: { color: string; label?: string }
): T {
  return produce(data, (draft) => {
    const payload = draft.data;
    if (!payload || !isSingleValueData(payload)) return;

    const steps: StepOptions[] = [];
    for (const item of payload.values) {
      if (item.value === null || item.value === undefined || Number.isNaN(item.value)) continue;
      steps.push({
        value: item.value,
        color,
        // Prefer the query's alias when present, otherwise fall back to the
        // series formattedName (which already has any per-series aliasing
        // applied) and finally the raw label-set name.
        name: label?.length ? label : (item.formattedName ?? item.name),
      });
    }

    if (steps.length === 0) return;

    if (!payload.metadata) payload.metadata = {};
    const dynamic: GenericThresholdOptions<StepOptions> = {
      mode: 'Absolute',
      steps,
    };
    payload.metadata.thresholds = dynamic;

    // The query is being rendered as a threshold, not as data, so clear the
    // scalar values from the payload. Otherwise downstream panels (Gauge, Stat,
    // Bar) would render each scalar as a primary data item (gauge needle, stat
    // tile, bar) in addition to the threshold band.
    payload.values = [];
  });
}

/**
 * Tag every returned series with `config.threshold` so panel renderers can draw
 * dashed reference series and exclude them from stack groups, and stack-total math.
 *
 * For `SingleValueTimeSeries` we additionally emit a `metadata.thresholds`
 * step per reduced value so panels can tint the primary tile's value text /
 * background once that tile's value crosses the threshold.
 */
function applySeriesBehavior<T extends QueryPluginGetDataBasicResponse<SupportedDataType>>(
  data: T,
  { color, label }: { color: string; label?: string }
): T {
  return produce(data, (draft) => {
    const payload = draft.data;
    if (!payload) return;

    const tag = (config: { threshold?: { color: string; label?: string } } | undefined) => {
      if (!config) return;
      config.threshold = { color };
      if (label?.length) {
        config.threshold.label = label;
      }
    };

    // TimeSeries shape
    if ('series' in payload && Array.isArray(payload.series)) {
      for (const series of payload.series) {
        if (!series.config) series.config = {};
        tag(series.config);
      }
      return;
    }

    // SingleValueTimeSeries shape (StatChart sparkline)
    if ('values' in payload && Array.isArray(payload.values)) {
      const steps: StepOptions[] = [];
      for (const item of payload.values) {
        if (!item.config) item.config = {};
        tag(item.config);
        if (item.value !== null && item.value !== undefined && !Number.isNaN(item.value)) {
          steps.push({
            value: item.value,
            color,
            name: label?.length ? label : (item.formattedName ?? item.name),
          });
        }
      }

      if (steps.length > 0) {
        if (!payload.metadata) payload.metadata = {};
        payload.metadata.thresholds = { mode: 'Absolute', steps };
      }
    }
  });
}
